import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { StorageNotificationTemplate, TYPE_CONFIGS } from "../typeConfig";
import TemplateViewSectionContent from "./TemplateViewSectionContent";

type Props = {
  template: StorageNotificationTemplate | null;
  onClose: () => void;
};

export default function TemplateViewDialog({ template, onClose }: Props) {
  const classes = useStyles();
  const { t } = useTranslation("storage");
  const [activeTab, setActiveTab] = useState(0);

  if (!template) return null;

  const activeSections = TYPE_CONFIGS.filter(
    tc => template[tc.formKey as keyof StorageNotificationTemplate]
  );

  const currentSection = activeSections[activeTab];

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant="h6">{template.name}</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {activeSections.length > 0 && (
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          className={classes.tabs}
        >
          {activeSections.map(tc => (
            <Tab key={tc.type} label={t(`notificationTemplates.type.${tc.formKey}.label`)} />
          ))}
        </Tabs>
      )}

      <DialogContent className={classes.content}>
        {activeSections.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {t("notificationTemplates.view.noActiveSections")}
          </Typography>
        ) : (
          <TemplateViewSectionContent
            tc={currentSection}
            template={template}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  paper: {
    height: 500,
  },
  title: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingLeft: 16,
  },
  content: {
    padding: "16px 24px",
  },
}));
